import React from "react";
import {
  IconButton,
  TextField,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  FormControlLabel,
  Checkbox,
  InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { BootstrapTooltip } from "../Tooltip";
import { PdfBookmarkItem } from "./pdfBookmarks";
import { PDFViewerThumbnails } from "./PDFViewerThumbnails";

export type PdfSidebarPanel = "search" | "bookmarks" | "thumbnails" | null;

type PDFViewerSidebarProps = {
  classes: Record<string, string>;
  isFullscreen?: boolean;
  activePanel: PdfSidebarPanel;
  onPanelChange: (panel: PdfSidebarPanel) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onClearSearch: () => void;
  searchMatchCase: boolean;
  searchWholeWord: boolean;
  onSearchMatchCaseChange: (value: boolean) => void;
  onSearchWholeWordChange: (value: boolean) => void;
  onSearch: () => void;
  onSearchPrev: () => void;
  onSearchNext: () => void;
  searchMatchLabel: string;
  isSearching: boolean;
  onCopySelection: () => void;
  copyMessage: string;
  bookmarks: PdfBookmarkItem[];
  onBookmarkClick: (bookmark: PdfBookmarkItem) => void;
  hasSearchResults: boolean;
  onSearchEnterKey: () => void;
  onSearchShiftEnter: () => void;
  pdfDoc: any;
  totalPages: number;
  currentPage: number;
  onThumbnailPageSelect: (pageNo: number) => void;
};

export const PDFViewerSidebar = (props: PDFViewerSidebarProps) => {
  const {
    classes,
    isFullscreen = false,
    activePanel,
    onPanelChange,
    searchQuery,
    onSearchQueryChange,
    onClearSearch,
    searchMatchCase,
    searchWholeWord,
    onSearchMatchCaseChange,
    onSearchWholeWordChange,
    onSearch,
    onSearchPrev,
    onSearchNext,
    searchMatchLabel,
    isSearching,
    onCopySelection,
    copyMessage,
    bookmarks,
    onBookmarkClick,
    hasSearchResults,
    onSearchEnterKey,
    onSearchShiftEnter,
    pdfDoc,
    totalPages,
    currentPage,
    onThumbnailPageSelect
  } = props;

  const togglePanel = (panel: PdfSidebarPanel) => {
    onPanelChange(activePanel === panel ? null : panel);
  };

  const sidebarClassName = [classes.pdfSidebar, isFullscreen && classes.pdfSidebarFullscreen]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={sidebarClassName}>
      <div className={classes.pdfSidebarRail}>
        <div className={classes.pdfSidebarRailItem}>
          <BootstrapTooltip title="Search" placement="right" arrow>
            <IconButton
              aria-label="pdf-search"
              className={`${classes.pdfSidebarRailIcon} ${classes.pdfSidebarRailIconSearch} ${
                activePanel === "search" ? classes.pdfSidebarIconActive : ""
              }`}
              onClick={() => togglePanel("search")}
            >
              <SearchIcon />
            </IconButton>
          </BootstrapTooltip>
        </div>
        <div className={classes.pdfSidebarRailItem}>
          <BootstrapTooltip title="Bookmarks" placement="right" arrow>
            <IconButton
              aria-label="pdf-bookmarks"
              className={`${classes.pdfSidebarRailIcon} ${
                activePanel === "bookmarks" ? classes.pdfSidebarIconActive : ""
              }`}
              onClick={() => togglePanel("bookmarks")}
            >
              <BookmarksOutlinedIcon />
            </IconButton>
          </BootstrapTooltip>
        </div>
        <div className={classes.pdfSidebarRailItem}>
          <BootstrapTooltip title="Page thumbnails" placement="right" arrow>
            <IconButton
              aria-label="pdf-thumbnails"
              className={`${classes.pdfSidebarRailIcon} ${
                activePanel === "thumbnails" ? classes.pdfSidebarIconActive : ""
              }`}
              onClick={() => togglePanel("thumbnails")}
            >
              <ViewModuleOutlinedIcon />
            </IconButton>
          </BootstrapTooltip>
        </div>
        <div className={classes.pdfSidebarRailItem}>
          <BootstrapTooltip title="Copy selected text" placement="right" arrow>
            <IconButton
              aria-label="pdf-copy-text"
              className={classes.pdfSidebarRailIcon}
              onClick={onCopySelection}
            >
              <ContentCopyIcon />
            </IconButton>
          </BootstrapTooltip>
        </div>
      </div>

      {activePanel && (
        <div className={classes.pdfSidebarPanel}>
          {activePanel === "search" && (
            <div className={`${classes.pdfSidebarPanelBody} ${classes.pdfSidebarSearchPanel}`}>
              <Typography variant="subtitle2" className={classes.pdfSidebarTitle}>
                Search
              </Typography>
              <TextField
                size="small"
                fullWidth
                margin="dense"
                className={classes.pdfSidebarSearchField}
                placeholder="Find in document"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (e.shiftKey) {
                    if (hasSearchResults) onSearchShiftEnter();
                    return;
                  }
                  onSearchEnterKey();
                }}
                InputProps={{
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={onClearSearch}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }}
              />
              <div className={classes.pdfSidebarSearchOptions}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={searchMatchCase}
                      onChange={(e) => onSearchMatchCaseChange(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Match case</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={searchWholeWord}
                      onChange={(e) => onSearchWholeWordChange(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Whole words</Typography>}
                />
              </div>
              <div className={classes.pdfSidebarActions}>
                <Button size="small" variant="outlined" onClick={onSearch} disabled={isSearching}>
                  Find
                </Button>
                <Button size="small" onClick={onSearchPrev} disabled={!searchMatchLabel}>
                  Prev
                </Button>
                <Button size="small" onClick={onSearchNext} disabled={!searchMatchLabel}>
                  Next
                </Button>
              </div>
              <Typography variant="caption" color="textSecondary" display="block">
                {isSearching ? "Searching..." : searchMatchLabel || "No results"}
              </Typography>
              <div className={classes.pdfSidebarFootnotes}>
                <Typography variant="caption" className={classes.pdfSidebarFootnote}>
                  Enter: Next
                </Typography>
                <Typography variant="caption" className={classes.pdfSidebarFootnote}>
                  Shift+Enter: Previous
                </Typography>
              </div>
            </div>
          )}

          {activePanel === "bookmarks" && (
            <div className={classes.pdfSidebarPanelBody}>
              <Typography variant="subtitle2" className={classes.pdfSidebarTitle}>
                Bookmarks
              </Typography>
              {bookmarks.length > 0 ? (
                <List dense disablePadding className={classes.pdfBookmarkList}>
                  {bookmarks.map((bookmark, index) => (
                    <ListItemButton
                      key={`${bookmark.title}-${index}`}
                      onClick={() => onBookmarkClick(bookmark)}
                      sx={{ pl: 1 + bookmark.level * 1.5 }}
                    >
                      <ListItemText
                        primary={bookmark.title}
                        primaryTypographyProps={{ fontSize: 13 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No bookmarks in this document
                </Typography>
              )}
            </div>
          )}

          {activePanel === "thumbnails" && (
            <PDFViewerThumbnails
              classes={classes}
              pdfDoc={pdfDoc}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageSelect={onThumbnailPageSelect}
            />
          )}

          {copyMessage && (
            <Typography variant="caption" className={classes.pdfCopyMessage}>
              {copyMessage}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
